import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoUNEA } from '../../components/common/Logo';
import MIcon from '../../components/common/MIcon';
import PortalNotice from '../../components/common/PortalNotice';
import TrackForm from '../../components/common/TrackForm';
import FAQSection from '../../components/common/FAQSection';
import { useT } from '../../context/TenantContext';
import { useFrontDeskAuth } from '../../context/FrontDeskAuthContext';
import FrontDeskLogin from './FrontDeskLogin';
import './frontdesk.css';

// Preguntas frecuentes del portal: ocultas por decisión. Cambiar a true para mostrarlas de nuevo.
const SHOW_FAQ = false;

// Las dos que más se usan van arriba, más grandes.
const BIG_CARDS = [
  {
    key: 'soporte',
    icon: 'support_agent',
    title: 'Soporte IT - Ticket',
    desc: 'Algo no funciona: hardware, red, software o cualquier otra incidencia técnica.',
    action: (navigate) => navigate('/nuevo-ticket'),
  },
  {
    key: 'material',
    icon: 'inventory_2',
    title: 'Préstamo de material',
    desc: 'Pide prestado un portátil, proyector u otro equipo por un tiempo determinado.',
    action: (navigate) => navigate('/loans'),
  },
];

const SMALL_CARDS = [
  {
    key: 'telefonia',
    icon: 'call',
    title: 'Extensiones y telefonía IP',
    desc: 'Alta, baja o cambio de ubicación de una extensión.',
    action: (navigate) => navigate('/nuevo-ticket?category=Comunicaciones&subcategory=Alta%2Fbaja%20de%20extensi%C3%B3n'),
  },
  {
    key: 'cuentas',
    icon: 'badge',
    title: 'Altas y bajas de cuentas',
    desc: 'Alta, baja o modificación de accesos.',
    action: (navigate) => navigate('/nuevo-ticket?category=Accesos'),
  },
  {
    key: 'evento',
    icon: 'event',
    title: 'Agendar evento con soporte técnico',
    desc: 'Videollamada, examen con vigilancia u otro evento.',
    action: (navigate) => navigate('/nuevo-ticket?category=Eventos'),
  },
];

function RequestCard({ icon, title, desc, onClick, big }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
        gap: big ? 14 : 12, padding: big ? '28px 26px' : '20px 18px', width: '100%',
        minHeight: big ? 160 : 150, height: '100%', boxSizing: 'border-box',
        background: 'var(--fd-bg)', border: 'none',
        borderRadius: 'var(--radius)', cursor: 'pointer',
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'all .15s ease',
      }}
    >
      <div style={{
        width: big ? 52 : 42, height: big ? 52 : 42, borderRadius: big ? 13 : 10, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <MIcon name={icon} size={big ? 28 : 22} style={{ color: 'var(--fd-accent)' }} />
      </div>
      <div style={{ fontSize: big ? 17 : 14, fontWeight: 700, color: 'var(--fd-text)', lineHeight: 1.3 }}>{title}</div>
      <div style={{ fontSize: big ? 13 : 11.5, color: 'var(--fd-muted)', lineHeight: 1.5 }}>{desc}</div>
    </button>
  );
}

export default function RequestHome() {
  const navigate = useNavigate();
  const { t } = useT();
  const { canAccess, fdLoading } = useFrontDeskAuth();
  const [authError] = React.useState(() => new URLSearchParams(window.location.search).get('auth_error'));
  const [slaData, setSlaData] = React.useState(null);

  React.useEffect(() => {
    fetch('/api/sla/estimated').then(r=>r.json()).then(data => {
      const map = {};
      data.forEach(d => { map[d.priority] = d; });
      setSlaData(map);
    }).catch(()=>{});
  }, []);

  const fmtMin = (m) => m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m/60*10)/10}h` : `${Math.round(m/1440*10)/10}d`;

  if (fdLoading) return null;
  if (!canAccess) return <FrontDeskLogin error={authError} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--fd-bg)' }}>
      <header className="fd-appbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <LogoUNEA height={32} style={{ display: 'block' }} />
          <div style={{ width: 1, height: 28, background: 'var(--fd-border)', margin: '0 16px' }} />
          <span style={{ fontSize: 15, fontWeight: 400, color: 'var(--fd-text)' }}>HelpDesk TI</span>
        </div>
      </header>

      <PortalNotice />

      <div className="fd-two-col">

        {/* ── COLUMNA IZQUIERDA — igual que en el formulario de ticket ── */}
        <div className="fd-col-left">
          <div className="fd-card fd-card-side">
            <h1 className="fd-side-title">
              ¿Cómo podemos<br/>
              <em style={{fontStyle:'normal',color:'var(--fd-accent)'}}>{t('fd.hero_accent')}</em>
            </h1>
            <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.7,margin:0}}>
              Elige qué necesitas a la derecha y te guiamos paso a paso.
            </p>

            <div className="fd-side-section" style={{borderTop:'none',paddingTop:0}}>
              <div style={{height:1,background:'var(--fd-border)',margin:'20px 0'}}/>
              <div className="fd-side-section-title">{t('fd.have_ticket')}</div>
              <TrackForm />
            </div>
          </div>

          <div className="fd-card fd-card-side">
            <div style={{fontSize:14,fontWeight:500,color:'var(--fd-text)',marginBottom:16}}>{t('fd.more_info')}</div>
            <div className="fd-side-section" style={{borderTop:'none',paddingTop:0,marginTop:0}}>
              <div className="fd-side-section-title">Tiempos de respuesta por prioridad</div>
              {[
                {prio:'critical', dot:'#B91C1C', label:t('priority.critical')},
                {prio:'high',     dot:'#B45309', label:t('priority.high')},
                {prio:'medium',   dot:'#0085CA', label:t('priority.medium')},
                {prio:'low',      dot:'#15803D', label:t('priority.low')},
              ].map(s => {
                const d = slaData?.[s.prio];
                const overloaded = d && d.load_factor > 1.5;
                return (
                  <div key={s.prio} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,marginBottom:8}}>
                    <span style={{width:8,height:8,borderRadius:'50%',background:s.dot,flexShrink:0}}/>
                    <span style={{color:'var(--fd-text)',flex:1}}>{s.label}</span>
                    <span style={{fontWeight:700,color:overloaded?'#B91C1C':s.dot}}>
                      {d?.estimated_min ? fmtMin(d.estimated_min) : '…'}
                    </span>
                  </div>
                );
              })}
              <div style={{fontSize:10,color:'#9CA3AF',marginTop:6,paddingTop:6,display:'flex',alignItems:'center',gap:4}}>
                <MIcon name="science" size={12} style={{marginRight:4}}/> Estimación beta · puede variar
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD DERECHO ANCHO — selector de necesidad ── */}
        <div className="fd-col-right fd-card fd-card-main">
          <div style={{ padding: 'clamp(24px, 4vw, 40px)' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--fd-text)', marginBottom: 6 }}>
              ¿Qué necesitas?
            </h1>
            <p style={{ fontSize: 13.5, color: 'var(--fd-muted)', marginBottom: 28 }}>
              Elige una opción para empezar. Si tienes dudas, la más habitual es "Soporte IT".
            </p>

            {/* Fila 1 — las dos opciones más usadas, más grandes */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {BIG_CARDS.map(c => (
                <RequestCard key={c.key} big icon={c.icon} title={c.title} desc={c.desc} onClick={() => c.action(navigate)} />
              ))}
            </div>

            {/* Fila 2 — el resto, más pequeñas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {SMALL_CARDS.map(c => (
                <RequestCard key={c.key} icon={c.icon} title={c.title} desc={c.desc} onClick={() => c.action(navigate)} />
              ))}
            </div>
          </div>
        </div>

      </div>

      {SHOW_FAQ && (
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px, 4vw, 48px) 40px' }}>
          <FAQSection />
        </div>
      )}
    </div>
  );
}
